<template>
  <div>
    <article class="project-analysis-item__wrapper" :inert="modalOpen" :aria-hidden="modalOpen">
      <AnalysisSettings
        ref="AnalysisSettings"
        :propReadOnly="propReadOnly"
        :statusDisableActionBtns="statusDisableActionBtns"
        :index="index"
        :thisAnalysisName="driversAnalysisTitle"
        :isDriversAnalysis="true"
        :viableDriversModel="viableModels"
        :savedNodes="
          this.driversAnalysisObject.saved_node_ids ? this.driversAnalysisObject.saved_node_ids : []
        "
        :filterParams="this.chartData ? this.chartData.filter_params : null"
        :contentChanged="this.contentChanged"
        :filterTop="this.filterTop"
        :isWeighted="isWeighted"
        :lastModified="
          formatDateTime(driversAnalysisObject.last_modified_on, $store.getters.getLanguage)
        "
        @toggled="isExpanded = !isExpanded"
        @on-change-drivers-model="onChangeDriversModel"
        @is-selected-var-visible="isSelectVarVisible = true"
        @changeIsLoadingChartData="isLoadingChartData = true"
        @changeIsLoadingChartDataOff="isLoadingChartData = false"
        @filterTop="updateFilterTop"
        @changeAnalysisTitle="renameAnalysis"
      ></AnalysisSettings>
      <div v-if="isExpanded">
        <LoadingSpinner
          :isLoading="isLoadingChartData"
          :componentStyle="true"
          style="position: relative"
          :style="{ 'min-height': minHeight }"
        />
      </div>

      <!--------------------------------------------
      |
      | CHARTS
      |
      --------------------------------------------->
      <div style="display: flex; flex-direction: row">
        <DriversCharts
          v-if="isExpanded && !isLoadingChartData"
          :skipMatrix="skipMatrix"
          :dependent-variable-title="dependentVariableTitle"
          :chart-data="filteredChartData"
          :isLoading="isLoadingChartData"
        />
        <InsightsFeed
          v-if="isExpanded && !isLoadingChartData"
          :chart_insights="
            driversAnalysisObject.chart_insights ? driversAnalysisObject.chart_insights : []
          "
        />
        <FindInsightsButton
          v-if="
            isExpanded &&
            !isLoadingChartData &&
            (!driversAnalysisObject.chart_insights || !driversAnalysisObject.chart_insights.length)
          "
          style="margin-right: 1rem"
          @refresh_for_insights="simpleRefresh"
        />
      </div>
    </article>
    <!--------------------------------------------
    |
    | VARIABLES MODAL
    |
    --------------------------------------------->
    <DriversVariables
      :dependent-variable-title="dependentVariableTitle"
      :dependent-questions="selectedDependentVars"
      :independent-questions="selectedIndependentVars"
      :show-drivers-variables="isSelectVarVisible"
      @modify-vars="modifyVars"
      @close-drivers-variables="isSelectVarVisible = false"
    />
  </div>
</template>

<script>
// Components
import DriversCharts from './Components/DriversCharts.vue';
import DriversVariables from './Components/VariablesModal.vue';
import AnalysisSettings from '@/components/Project/ProjectAnalysis/Components/AnalysisSettings.vue';
import LoadingSpinner from '@/components/UI/Spinner.vue';
import InsightsFeed from '@/components/Project/ProjectAnalysis/Components/InsightsFeed.vue';
import FindInsightsButton from '@/components/Project/ProjectAnalysis/Components/FindInsightsButton.vue';

// Mixins
import DatasetDetailsMixin from '@/utils/mixins/datasetDetailsMixin.js';
import DriversAnalysisMixin from './Mixins/driversAnalysisMixin.js';
import ProjectMixin from '@/components/Project/Mixins/projectMixin.js';
import ProjectReportMixin from '@/components/Project/Mixins/projectReportMixin.js';
import FiltersMixin from '@/utils/mixins/userMixin.js';
import AnalysisMixin from '@/components/Project/ProjectAnalysis/Components/analysisMixin.js';

// Helpers
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'ProjectAnalysisDriversItem',
  mixins: [
    DatasetDetailsMixin,
    DriversAnalysisMixin,
    ProjectMixin,
    ProjectReportMixin,
    FiltersMixin,
    AnalysisMixin,
  ],
  props: {
    index: {
      type: Number,
      required: true,
    },
    expandThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    renameThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    saveThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    slideName: {
      default: () => '',
      type: String,
    },
    slideForSaving: {
      default: () => {},
      type: Object,
    },
    setToChangeFilterTop: {
      default: () => false,
      type: Boolean,
    },
    selectedDriversFilterTop: {
      default: () => -1,
      type: Number,
    },
    setToUpdateDriversModel: {
      default: () => false,
      type: Boolean,
    },
    modifiedDrivers: {
      default: () => '',
      type: String,
    },
    setToUpdateTimeFilter: {
      default: () => false,
      type: Boolean,
    },
    selectedTimeFilter: {
      default: () => 'All data',
      type: String,
    },
    selectedTimeSeries: {
      default: () => 'Days',
      type: String,
    },
    setToUpdateTimeSeries: {
      default: () => false,
      type: Boolean,
    },
  },
  components: {
    DriversCharts,
    DriversVariables,
    AnalysisSettings,
    LoadingSpinner,
    FindInsightsButton,
    InsightsFeed,
  },
  data() {
    return {
      savedSlide: '',
      selectedDriversModel: '',
      driversAnalysisObject: {},
      dependentVariableTitle: 'overall satisfaction',
      isSelectVarVisible: false,
      selectedDependentVars: [],
      selectedIndependentVars: [],
      uuid: this.$pigeonline.createUUID(),
      isExpanded: false,
      contentChanged: false,
      filteredChartData: {},
      filterTop: 10,
      isLoadingChartData: false,
    };
  },
  async created() {
    this.driversAnalysisObject = _.cloneDeep(this.project.driversAnalysis[this.index]);
    await this.checkForOldSavedNodes(); // Prior to 1.10.0, old nodes were saved directly, wasting space. Revert back to IDs
    if (
      this.driversAnalysisObject.filter_params &&
      Object.keys(this.driversAnalysisObject.filter_params).length
    ) {
      this.filter_params = this.driversAnalysisObject.filter_params;
    }
    this.setVariables(
      this.driversAnalysisObject.dependent_variable_id,
      this.driversAnalysisObject.independent_variables_ids,
      this.driversAnalysisObject.dependent_variable_title,
    );
    // if no chart data saved or performance type
    // was recently updated fetch chart data & update project
    if (
      !this.driversAnalysisObject.chart_data ||
      (this.projectPerformanceType &&
        this.projectPerformanceType !== this.driversAnalysisObject.saved_performance_type)
    ) {
      await this.setChartData(
        this.driversAnalysisObject.dependent_variable_id,
        this.driversAnalysisObject.independent_variables_ids,
        this.selectedDriversModel,
      );
      await this.saveDriversToProject(this.driversAnalysisObject.uuid);
    } else {
      this.chartData = this.driversAnalysisObject.chart_data;
    }
    if (this.driversAnalysisObject.filterTop) {
      this.filterTop = this.driversAnalysisObject.filterTop;
    }
    this.filteredChartData = this.chartData;
    // Note: The below requires a 'close' and open due to responsivity, requires a fix
    this.updateFilteredChartData();

    this.applyName();
    this.$emit('updateFilterTop', this.filterTop);
    if (
      this.driversAnalysisObject.chart_data &&
      this.driversAnalysisObject.chart_data.estimator_name
    ) {
      this.$emit('updateModels', this.driversAnalysisObject.chart_data.estimator_name);
    }
  },
  computed: {
    driversAnalysisTitle() {
      let driversTitle = '';
      if (this.driversAnalysisObject) {
        if (
          this.driversAnalysisObject.analysis_title &&
          this.driversAnalysisObject.analysis_title.length
        ) {
          driversTitle = this.driversAnalysisObject.analysis_title;
        }
      }
      return driversTitle;
    },
    statusDisableActionBtns() {
      return this.isLoadingChartData || this.propReadOnly;
    },
    // not directly used, save for bilingual expansion
    visiblePlaceholder() {
      return this.checkTranslationCache(
        'Please name this slide',
        'slide-name',
        this.$store.getters.getLanguage,
      );
    },
    viableModels() {
      let models = [];
      let dvTypes = [];
      let ivTypes = [];
      if (this.selectedDependentVars) {
        for (let i = 0; i < this.selectedDependentVars.length; i++) {
          this.$store.getters['datasetDetails/getClientQuestions'].reduce((allqs, q) => {
            if (this.selectedDependentVars[i].includes(q._id.$oid)) {
              if (q.approved_data_type) {
                // Check if categorical, then check if binary or multiclass
                if (q.approved_data_type === 'CAT') {
                  if (q.unique_values.length === 2) {
                    dvTypes.push('CAT--binary');
                  } else {
                    dvTypes.push('CAT--multiclass');
                  }
                } else {
                  dvTypes.push(q.approved_data_type);
                }
              }
            }
          });
        }
        for (let i = 0; i < this.selectedIndependentVars.length; i++) {
          this.$store.getters['datasetDetails/getClientQuestions'].reduce((allqs, q) => {
            if (this.selectedIndependentVars[i].includes(q._id.$oid)) {
              if (q.approved_data_type) {
                ivTypes.push(q.approved_data_type);
              }
            }
          });
        }
        if (ivTypes.length && dvTypes.length) {
          if (dvTypes.length === 1) {
            if (dvTypes.includes('CAT--binary')) {
              models.push('random_forest_classification');
              models.push('logistic_regression');
            }
            if (dvTypes.includes('CAT--multiclass')) {
              models.push('random_forest_classification');
            }
          }
          if (!dvTypes.includes('CAT--binary') && !dvTypes.includes('CAT--multiclass')) {
            models.push('random_forest_regression');
            models.push('elastic_net_regression');
          }
        }
      }

      return models;
    },
    skipMatrix() {
      let skipMatrix = false;
      let ivTypes = [];
      for (let i = 0; i < this.selectedIndependentVars.length; i++) {
        this.$store.getters['datasetDetails/getClientQuestions'].reduce((allqs, q) => {
          if (this.selectedIndependentVars[i].includes(q._id.$oid)) {
            if (q.approved_data_type) {
              ivTypes.push(q.approved_data_type);
            }
          }
        });
      }
      let checkingForUnique = [];
      if (ivTypes.length) {
        for (let i = 0; i < ivTypes.length; i++) {
          if (!checkingForUnique.includes(ivTypes[i])) {
            checkingForUnique.push(ivTypes[i]);
          }
        }
      }
      if (checkingForUnique.length) {
        if (checkingForUnique.length > 1) {
          skipMatrix = true;
        }
        for (let i = 0; i < checkingForUnique.length; i++) {
          if (!checkingForUnique[i].includes('SCALE')) {
            skipMatrix = true;
          }
        }
      }
      return skipMatrix;
    },
    isFiltered() {
      if (this.chartData) {
        if (this.chartData.filter_params) {
          return true;
        }
      }
      return false;
    },
    minHeight() {
      if (this.isLoadingChartData) {
        return '5em';
      } else {
        return '0';
      }
    },
    isWeighted() {
      if (this.chartData) {
        if (this.chartData.is_weight_applied) {
          return true;
        }
      }
      return false;
    },
  },
  methods: {
    // In legacy objects, saved_node_ids were actually full nodes, wasteful. Revert back to ids. These are just used by AnalysisSettings to recolor
    async checkForOldSavedNodes() {
      if (
        this.driversAnalysisObject.saved_node_ids &&
        this.driversAnalysisObject.saved_node_ids.length
      ) {
        let new_ids = [];
        for (let i = 0; i < this.driversAnalysisObject.saved_node_ids.length; i++) {
          if (
            Object.keys(this.driversAnalysisObject.saved_node_ids[i]).length &&
            this.driversAnalysisObject.saved_node_ids[i].uuid
          ) {
            new_ids.push(this.driversAnalysisObject.saved_node_ids[i].uuid);
          }
        }
        if (new_ids.length) {
          this.driversAnalysisObject.saved_node_ids = new_ids;
          this.$emit('refresh');
          //this.saveTextToProject()
        }
      }
    },
    async updateFilterTop(number) {
      if (!number) return;
      this.filterTop = parseInt(number);
      await this.saveDriversToProject(this.driversAnalysisObject.uuid);
    },
    async updateFilteredChartData() {
      if (this.chartData) {
        if (this.chartData.num_drivers) {
          let storedData = this.deepCloneObj(this.chartData);
          let num_drivers = this.chartData.num_drivers;
          let new_num_drivers;
          if (num_drivers.length) {
            new_num_drivers = num_drivers.slice(0, this.filterTop);
            this.filteredChartData.num_drivers = new_num_drivers;
            if (this.$refs.AnalysisSettings) {
              this.$refs.AnalysisSettings.toggleContent();
            }
            this.chartData = storedData;
          }
        }
      }
    },
    updateSavedSlide(savedSlide) {
      this.savedSlide = savedSlide;
    },
    filterClientQuestions(questionIDs) {
      return this.clientQuestions.filter((q) => questionIDs.includes(q._id.$oid));
    },
    // In legacy objects, analyses had no such name
    applyName() {
      if (
        this.dependentVariableTitle.length &&
        (!this.driversAnalysisObject.analysis_title ||
          !this.driversAnalysisObject.analysis_title.length)
      ) {
        let driversTitle = 'Predicting ' + this.dependentVariableTitle;
        this.renameAnalysis(driversTitle);
      }
    },
    async renameAnalysis(title) {
      if (this.propReadOnly) return;
      const indx = this.project.driversAnalysis.findIndex(
        (d) => d.uuid == this.driversAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.driversAnalysis[indx].analysis_title = title;
        this.project.driversAnalysis[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },
    /**
     * backwards compatability: project.driversAnalysis updated from object
     * to array, replace it with an array
     */
    async handleBackwardsCompatibility() {
      if (!this.project.driversAnalysis) {
        this.project.driversAnalysis = [];
        await this.cleanAndSaveProject();
        // await this.saveProject(this.project)
      } else if (
        !Array.isArray(this.project.driversAnalysis) &&
        Object.keys(this.project.driversAnalysis).length > 0
      ) {
        this.project.driversAnalysis = new Array(this.driversAnalysisObject);
        await this.cleanAndSaveProject();
        // await this.saveProject(this.project)
      }
    },
    /************
     *
     * Setters
     *
     ***********/
    setVariables(dependentVar, independentVars, dependentVarTitle) {
      this.dependentVariableTitle = dependentVarTitle;
      this.selectedDependentVars = dependentVar;
      this.selectedIndependentVars = independentVars;
    },
    async setChartData(dependentVar, independentVars, algorithm) {
      let time_series = 'None';
      if (this.driversAnalysisObject.time_series) {
        time_series = this.driversAnalysisObject.time_series;
      }
      let filter_params = {};
      if (this.driversAnalysisObject.filter_params) {
        filter_params = this.driversAnalysisObject.filter_params;
      }
      this.isLoadingChartData = true;
      this.chartData = {};
      this.chartData = await this.$_driversAnalysisMixin_fetchChartData(
        this.project.id,
        this.dataset._id.$oid,
        dependentVar,
        independentVars,
        algorithm,
        filter_params,
        time_series,
      );
      this.driversAnalysisObject.chart_insights = [];
      this.isLoadingChartData = false;
    },
    /************
     *
     * Saving methods
     *
     ***********/
    async modifyVars(dependent, independent, model) {
      if (model === '') {
        this.selectedDriversModel = '';
      }
      this.setVariables(
        dependent,
        independent,
        this.$_driversAnalysisMixin_getDependentVarTitle(dependent),
      );
      await this.setChartData(dependent, independent, model);
      this.saveDriversToProject(this.driversAnalysisObject.uuid);
      this.contentChanged = true;
      if (this.$refs.AnalysisSettings) {
        this.$refs.AnalysisSettings.toggleContent();
      }
      this.filteredChartData = this.chartData;
    },
    async save(selectedSlide, newSlideName) {
      if (this.propReadOnly) return;
      const uuid = await this.saveDriversToReport(selectedSlide, newSlideName);
      // this.project.updateStatus("analysisCompleted")
      // this is used to get all AnalysisSetting->SaveAnalysis to rerun collections
      if (!this.project.reportSaves) {
        this.project.reportSaves = 1;
      } else {
        this.project.reportSaves = this.project.reportSaves + 1;
      }
      await this.saveDriversToProject(uuid);
      this.$emit('refresh');
      this.contentChanged = false;
    },
    async saveDriversToReport(selectedSlide, newSlideName) {
      try {
        this.isLoadingChartData = true;
        let createdReportID = await this.createReportIfEmpty(selectedSlide, newSlideName); // the above function will do nothing under conditions of emptiness
        if (selectedSlide && selectedSlide.id) {
          createdReportID = selectedSlide.id;
        }
        let returnedHeader = await this.updateReportWithDriversHeader(createdReportID);
        // This report should only update matrix if the IVs are the same scale
        const returnObject = await this.updateReportWithDrivers(
          this.driversAnalysisObject.uuid,
          createdReportID,
          this.filteredChartData,
          this.dependentVariableTitle,
          this.skipMatrix,
        );
        let header_node_ids = returnedHeader.map((node) => node.uuid);
        let node_ids = returnObject.newNodes.map((node) => node.uuid);
        node_ids = node_ids.concat(header_node_ids);
        this.driversAnalysisObject.saved_node_ids = node_ids;
        this.isLoadingChartData = false;
        await this.$emit('updateSlides');
        this.onSuccessfulSave();
        return returnObject.uuid;
      } catch (e) {
        throw new Error('ProjectAnalysisDrivers:saveDriversToReport ' + e.message);
      }
    },
    async saveDriversToProject(uuid = null) {
      if (this.propReadOnly) return;
      let node_ids = [];
      if (this.driversAnalysisObject.saved_node_ids) {
        node_ids = this.driversAnalysisObject.saved_node_ids;
      }
      let time_filter_name = 'All data';
      if (this.driversAnalysisObject.time_filter_name) {
        time_filter_name = this.driversAnalysisObject.time_filter_name;
      }
      let time_series = 'None';
      if (this.driversAnalysisObject.time_series) {
        time_series = this.driversAnalysisObject.time_series;
      }
      let chart_insights = [];
      if (this.driversAnalysisObject.chart_insights) {
        chart_insights = this.driversAnalysisObject.chart_insights;
      }
      const newObject = {
        saved_performance_type: this.projectPerformanceType || 'scale-average',
        dependent_variable_id: this.selectedDependentVars,
        dependent_variable_title: this.dependentVariableTitle,
        independent_variables_ids: this.selectedIndependentVars,
        chart_data: this.chartData,
        chart_insights: chart_insights,
        saved_node_ids: node_ids,
        last_modified_on: moment().valueOf(),
        filterTop: this.filterTop,
        uuid: uuid || this.$pigeonline.createUUID(),
        time_filter_name: time_filter_name,
        time_series: time_series,
      };
      await this.handleBackwardsCompatibility();

      // find it in currently saved drivers, if there update, if not add it
      const indx = this.project.driversAnalysis.findIndex(
        (d) => d.uuid == this.driversAnalysisObject.uuid,
      );
      if (indx > -1) this.project.driversAnalysis.splice(indx, 1, _.cloneDeep(newObject));
      else this.project.driversAnalysis.unshift(_.cloneDeep(newObject));

      await this.cleanAndSaveProject();
      // await this.saveProject(this.project)
      this.driversAnalysisObject = _.cloneDeep(newObject);
    },
    onSuccessfulSave() {
      this.setConfirmText({
        btn: 'okay',
        title: 'Drivers saved',
        message: 'Drivers successfully saved to report.',
      });
      this.setConfirmType('success');
      this.setConfirmSourceComponent('drivers');
      this.setConfirmIsVisible(true);
    },
    async onChangeDriversModel(thisModel) {
      this.selectedDriversModel = thisModel;
      await this.modifyVars(
        this.selectedDependentVars,
        this.selectedIndependentVars,
        this.selectedDriversModel,
      );
      if (this.driversAnalysisObject.saved_node_ids.length) {
        this.contentChanged = true;
      }
    },
    async changeTimeFilter(timeFilter) {
      if (!timeFilter) return;

      if (this.dataset.created_on_column_id) {
        let question_id = this.dataset.created_on_column_id;
        let filter_params = {};
        if (
          this.driversAnalysisObject.filter_params &&
          Object.keys(this.driversAnalysisObject.filter_params).length &&
          this.driversAnalysisObject.filter_params['operands']
        ) {
          filter_params = this.deepCloneObj(this.driversAnalysisObject.filter_params);
        } else {
          filter_params = {
            client_question_id: question_id,
            operands: [],
            global_combine_action: 'and',
          };
        }
        const new_filter_params = await this.$_analysisMixin_calculateNewTimeFilters(
          filter_params,
          timeFilter,
        );
        this.driversAnalysisObject.filter_params = new_filter_params;
        this.driversAnalysisObject.time_filter_name = timeFilter;
        await this.setChartData(
          this.driversAnalysisObject.dependent_variable_id,
          this.driversAnalysisObject.independent_variables_ids,
          this.selectedDriversModel,
        );
        await this.saveDriversToProject(this.driversAnalysisObject.uuid);

        this.contentChanged = true;
        if (this.$refs.AnalysisSettings) {
          this.$refs.AnalysisSettings.toggleContent();
        }
        this.filteredChartData = this.chartData;
      }
    },
    async changeTimeSeries(timeSeries) {
      let time_series = 'None';
      if (timeSeries) {
        time_series = timeSeries;
      }
      this.driversAnalysisObject.time_series = time_series;
      await this.setChartData(
        this.driversAnalysisObject.dependent_variable_id,
        this.driversAnalysisObject.independent_variables_ids,
        this.selectedDriversModel,
      );
      this.saveDriversToProject(this.driversAnalysisObject.uuid);
      this.contentChanged = true;
      if (this.$refs.AnalysisSettings) {
        this.$refs.AnalysisSettings.toggleContent();
      }
      this.filteredChartData = this.chartData;
    },
    formatDateTime: (value, language) => {
      let currentLang = '';
      if (language) {
        currentLang = language;
      }
      if (value) {
        if (currentLang === 'fr') {
          return moment(value).locale('fr').format('MMMM D, YYYY hh:mm A').toString();
        } else {
          return moment(value).format('MMMM D, YYYY hh:mm A').toString();
        }
      }
    },
    async simpleRefresh() {
      if (this.project.driversAnalysis && this.project.driversAnalysis.length) {
        this.driversAnalysisObject = _.cloneDeep(this.project.driversAnalysis[this.index]);
      }
    },
  },
  watch: {
    filterTop: async function (newVal, oldVal) {
      if (oldVal && newVal) {
        if (oldVal !== newVal) {
          this.updateFilteredChartData();
        }
      }
    },
    activeTab: async function (val) {
      if (val === 'Drivers') {
        if (!this.chartData) {
          this.setVariables(
            this.driversAnalysisObject.dependent_variable_id,
            this.driversAnalysisObject.independent_variables_ids,
            this.driversAnalysisObject.dependent_variable_title,
          );
        }
        this.applyName();
      }
    },
    projectUpdatedPerformanceType: async function (type) {
      if (type && type !== this.driversAnalysisObject.saved_performance_type) {
        await this.setChartData(
          this.driversAnalysisObject.dependent_variable_id,
          this.driversAnalysisObject.independent_variables_ids,
          this.selectedDriversModel,
        );
        await this.saveDriversToProject(this.driversAnalysisObject.uuid);
      }
    },
    expandThisAnalysis: async function (val) {
      if (val) {
        this.isExpanded = true;
      } else {
        this.isExpanded = false;
      }
    },
    renameThisAnalysis: async function (val) {
      if (val) {
        if (this.$refs.AnalysisSettings) {
          this.$refs.AnalysisSettings.toggleRename();
        }
      }
    },
    saveThisAnalysis: async function (val) {
      if (val) {
        this.save(this.slideForSaving, this.slideName);
      }
    },
    setToChangeFilterTop: async function (val) {
      if (val) {
        this.updateFilterTop(this.selectedDriversFilterTop);
      }
    },
    viableModels: async function (val) {
      if (val) {
        this.$emit('updateViableModels', val);
      }
    },
    setToUpdateDriversModel: async function (val) {
      if (val) {
        await this.onChangeDriversModel(this.modifiedDrivers);
        this.$emit('updateViableModels', this.viableModels);
        if (this.$refs.AnalysisSettings) {
          this.$refs.AnalysisSettings.toggleContent();
        }
      }
    },
    setToUpdateTimeFilter: async function (val) {
      if (val) {
        await this.changeTimeFilter(this.selectedTimeFilter);
        this.$emit('updateTimeFilter', this.selectedTimeFilter);
      }
    },
    setToUpdateTimeSeries: async function (val) {
      if (val) {
        await this.changeTimeSeries(this.selectedTimeSeries);
        this.$emit('updateTimeSeries', this.selectedTimeSeries);
      }
    },
  },
};
</script>
